import create from 'zustand';
import {useShallow} from 'zustand/react/shallow';
import {getLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';
import {
  computeOnboardingSteps,
  ONBOARDING_STEPS,
  StepKey,
} from '@/Utils/onboardingSteps';

// _v2: the username step's summaryDone got stricter (a phone-number username
// no longer counts as chosen). Latched flags only ever accrete true, so the
// old key would keep every existing user's username step "done" forever —
// a fresh key lets the step re-derive from the new predicate.
const FLAGS_KEY = 'onboarding_progress_flags_v2';
// Persisted, so the welcome nudge fires exactly ONCE per user, ever — matching
// cc-learning-app. Ongoing prompting is handled by the always-visible surfaces
// instead (the header ring + completion %, the avatar dropdown row and the
// Settings card), which nag without interrupting.
const WELCOME_KEY = 'onboarding_welcome_seen';
// Same latch idiom as WELCOME_KEY: the "checklist complete -> persist
// learning_onboarding_status=1" POST (fired from OnboardingChecklistSheet)
// must happen at most ONCE per account, ever, so it can never re-fire or
// loop on reload/relogin.
const STATUS_PERSISTED_KEY = 'onboarding_status_persisted';

type Flags = Partial<Record<StepKey, boolean>>;

interface State {
  flags: Flags;
  summarySnapshot: any;
  welcomeSeen: boolean;
  statusPersisted: boolean;
  hydrated: boolean;
  // user_key the persisted flags/welcome flag belong to. Both storage keys are
  // browser-global, and flags are latch-only (never un-set), so without this a
  // second account signing in on the same browser inherited the first
  // account's 100%: the header completion bar stayed hidden and the welcome
  // nudge stayed suppressed. On web this is the norm, not an edge case.
  owner: string | null;
  // Incremented every time a profile save happens outside of the Settings page
  // (e.g. the onboarding popup's ProfileEditor). Settings watches this and
  // re-fetches so its form reflects the new values without a full page reload.
  lastProfileSave: number;
  bootstrap: () => Promise<void>;
  hydrateFromSummary: (summary: any) => void;
  markStepDone: (key: StepKey) => void;
  setWelcomeSeen: () => void;
  markStatusPersisted: () => void;
  bumpProfileSave: () => void;
}

// setLocalStorage no-ops on a falsy value, so we always persist a non-empty
// object (the __v guard also future-proofs the persisted shape).
const persistFlags = (flags: Flags, owner: string | null) =>
  setLocalStorage(FLAGS_KEY, {...flags, __v: 1, __owner: owner || ''});

export const useOnboardingProgressStore = create<State>((set, getState) => ({
  flags: {},
  summarySnapshot: {},
  welcomeSeen: false,
  statusPersisted: false,
  hydrated: false,
  owner: null,
  lastProfileSave: 0,

  bootstrap: async () => {
    const [savedFlags, savedWelcome, savedStatusPersisted] = await Promise.all([
      getLocalStorage(FLAGS_KEY),
      getLocalStorage(WELCOME_KEY),
      getLocalStorage(STATUS_PERSISTED_KEY),
    ]);
    const clean: Flags = {};
    if (savedFlags) {
      for (const step of ONBOARDING_STEPS) {
        if ((savedFlags as any)[step.key]) clean[step.key] = true;
      }
    }
    // The signed-in user isn't known yet at cold start; hydrateFromSummary
    // resets these if the owner turns out to be someone else.
    set({
      flags: clean,
      // {seen:false} is a truthy OBJECT, so a bare !!savedWelcome would read a
      // reset flag as "already seen" and swallow the nudge. Trust the `seen`
      // field when present; fall back to truthiness only for the legacy shape.
      welcomeSeen:
        savedWelcome && typeof savedWelcome === 'object' && 'seen' in savedWelcome
          ? !!(savedWelcome as any).seen
          : !!savedWelcome,
      statusPersisted:
        savedStatusPersisted &&
        typeof savedStatusPersisted === 'object' &&
        'persisted' in savedStatusPersisted
          ? !!(savedStatusPersisted as any).persisted
          : !!savedStatusPersisted,
      hydrated: true,
      owner:
        ((savedFlags as any)?.__owner ||
          (savedWelcome as any)?.owner ||
          (savedStatusPersisted as any)?.owner ||
          null) ||
        null,
    });
  },

  hydrateFromSummary: summary => {
    const nextOwner = summary?.user?.user_key
      ? String(summary.user.user_key)
      : null;
    const prevOwner = getState().owner;
    // Different account on this browser -> start their progress from scratch
    // rather than inheriting the previous user's latched flags.
    const switchedUser = !!nextOwner && !!prevOwner && nextOwner !== prevOwner;

    const next: Flags = switchedUser ? {} : {...getState().flags};
    for (const step of ONBOARDING_STEPS) {
      if (step.summaryDone && step.summaryDone(summary)) next[step.key] = true;
    }
    const owner = nextOwner || prevOwner;
    set({
      summarySnapshot: summary || {},
      flags: next,
      owner,
      ...(switchedUser ? {welcomeSeen: false, statusPersisted: false} : {}),
    });
    persistFlags(next, owner);
    if (switchedUser) {
      setLocalStorage(WELCOME_KEY, {seen: false, owner});
      setLocalStorage(STATUS_PERSISTED_KEY, {persisted: false, owner});
    }
  },

  markStepDone: key => {
    if (getState().flags[key]) return;
    const next = {...getState().flags, [key]: true};
    set({flags: next});
    persistFlags(next, getState().owner);
  },

  setWelcomeSeen: () => {
    set({welcomeSeen: true});
    setLocalStorage(WELCOME_KEY, {seen: true, owner: getState().owner});
  },

  markStatusPersisted: () => {
    set({statusPersisted: true});
    setLocalStorage(STATUS_PERSISTED_KEY, {
      persisted: true,
      owner: getState().owner,
    });
  },

  bumpProfileSave: () => set(s => ({lastProfileSave: s.lastProfileSave + 1})),
}));

// Derived selectors — recompute from the latched flags + latest summary.
export const useOnboardingProgress = () => {
  const [flags, summary] = useOnboardingProgressStore(
    useShallow(s => [s.flags, s.summarySnapshot]),
  );
  return computeOnboardingSteps(summary, flags);
};

export const useOnboardingPercent = () => useOnboardingProgress().percent;
export const useOnboardingSteps = () => useOnboardingProgress().steps;
export const useOnboardingComplete = () =>
  useOnboardingProgress().percent >= 100;

export default useOnboardingProgressStore;
